'use client'

import * as React from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { AnimatePresence, motion } from 'framer-motion'
import { cn } from '@/lib/utils'

enum ModalType {
  full = 'full',
  md = 'md'
}

// Modal
type ModalProps = React.PropsWithChildren<{
  show: boolean
  onDismiss: () => void
  dismissOnClickOutside?: boolean
  modalType?: string
  className?: string
  backdropClassName?: string
  lottieComponent?: React.ReactNode // Lottie animation component for background effects (expects a React component rendering Lottie JSON animation data)
}>

let dialogContainer: HTMLDivElement

function getEnsureDialogContainer() {
  if (!dialogContainer) {
    dialogContainer = document.createElement('div')
    dialogContainer.className = 'container'
    document.body.append(dialogContainer)
  }

  return dialogContainer
}

export const Modal = ({
  show,
  onDismiss,
  dismissOnClickOutside,
  children,
  modalType,
  className,
  backdropClassName,
  lottieComponent
}: ModalProps): React.ReactElement => {
  let style = 'p-0 z-40 '

  switch (modalType) {
    case ModalType.full:
      style += ` fixed top-0 left-0 w-[100vw] h-[100dvh] border-none bg-transparent`
      break
    case ModalType.md:
      style += ` fixed overflow-visible top-1/2 left-1/2 w-[90%] max-w-md max-h-[90%] p-md rounded-xs transform -translate-x-1/2 -translate-y-1/2 bg-surface border border-outline-variant shadow-[3px_3px_6px_0px_rgba(0,0,0,0.20)]`
      break
    default:
      break
  }

  style = cn(style, className)

  return (
    <Dialog.Root open={show}>
      <AnimatePresence>
        {show && (
          <Dialog.Portal forceMount container={getEnsureDialogContainer()}>
            <Dialog.Overlay className="fixed inset-0 z-30" asChild>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ type: 'spring', duration: 0.5 }}
                className={cn(`bg-surface-inverted-90 backdrop-blur-xl`, backdropClassName)}
              >
                {lottieComponent}
              </motion.div>
            </Dialog.Overlay>
            <Dialog.Title className="DialogTitle">Art Blocks Modal</Dialog.Title>

            <Dialog.Content
              onInteractOutside={() => {
                if (dismissOnClickOutside) {
                  onDismiss()
                }
              }}
              forceMount
              asChild
              className={`${style}`}
              aria-describedby="modal-description"
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ type: 'easeIn', duration: 0.3 }}
                className="pointer-events-none"
              >
                {children}
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  )
}
